import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutList,
  GripVertical,
  Eye,
  EyeOff,
  Pencil,
  Save,
  Loader2,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  useHomepageLayout,
  SECTION_DEFS,
  SectionConfig,
} from '../../lib/hooks/useHomepageLayout';
import {
  usePageLayouts,
  NAVIGATOR_LAYOUTS,
  APARTMENT_DETAILS_LAYOUTS,
  PageLayouts,
} from '../../lib/hooks/usePageLayouts';

const defMap = Object.fromEntries(SECTION_DEFS.map((d) => [d.key, d]));

const SectionsBuilder = () => {
  const { layout, loading, saveLayout } = useHomepageLayout();
  const { layouts, loading: layoutsLoading, saveLayouts } = usePageLayouts();
  const [savingLayout, setSavingLayout] = useState<keyof PageLayouts | null>(null);
  const [savedLayout, setSavedLayout] = useState<keyof PageLayouts | null>(null);
  const [items, setItems] = useState<SectionConfig[]>([]);
  const [dragged, setDragged] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });

  useEffect(() => {
    if (!loading) setItems(layout.sections);
  }, [layout, loading]);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  const toggle = (key: string) => {
    setItems((prev) => prev.map((s) => (s.key === key ? { ...s, enabled: !s.enabled } : s)));
  };

  const handleDrop = (targetKey: string) => {
    if (!dragged || dragged === targetKey) return;
    const from = items.findIndex((s) => s.key === dragged);
    const to = items.findIndex((s) => s.key === targetKey);
    if (from === -1 || to === -1) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setDragged(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatus({ type: null, message: '' });
    const ok = await saveLayout(items);
    setIsSaving(false);
    setStatus(
      ok
        ? { type: 'success', message: 'Подредбата е запазена успешно!' }
        : { type: 'error', message: 'Грешка при запазване' }
    );
    if (ok) setTimeout(() => setStatus({ type: null, message: '' }), 3000);
  };

  // Layout choice saves on its own — it is independent of the section order.
  const pickLayout = async (page: keyof PageLayouts, value: string) => {
    setSavingLayout(page);
    const ok = await saveLayouts({ [page]: value } as Partial<PageLayouts>);
    setSavingLayout(null);
    if (ok) {
      setSavedLayout(page);
      setTimeout(() => setSavedLayout(null), 2000);
    }
  };

  if (loading || layoutsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const layoutGroups = [
    {
      page: 'navigator' as const,
      title: 'Страница „Разпределения"',
      hint: 'Начинът, по който клиентът стига от сградата до конкретен обект.',
      options: NAVIGATOR_LAYOUTS as { value: string; label: string; description: string }[],
      current: layouts.navigator as string,
    },
    {
      page: 'apartmentDetails' as const,
      title: 'Страница на обект',
      hint: 'Подредбата на галерията, данните и бутона за запитване.',
      options: APARTMENT_DETAILS_LAYOUTS as { value: string; label: string; description: string }[],
      current: layouts.apartmentDetails as string,
    },
  ];

  return (
    <div className="p-6">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="flex items-center">
          <LayoutList className="w-8 h-8 text-primary mr-3" />
          <h1 className="text-2xl font-bold">Подредба на секциите</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Запазване...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Запази подредбата
            </>
          )}
        </button>
      </div>

      {status.type && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          {status.message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-1">Оформление на страниците</h2>
        <p className="text-sm text-gray-500 mb-6">
          Изберете как да изглеждат основните страници. Промяната е моментална и не засяга
          съдържанието — само подредбата и начина на разглеждане.
        </p>

        <div className="space-y-8">
          {layoutGroups.map((group) => (
            <div key={group.page}>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-gray-900">{group.title}</h3>
                {savingLayout === group.page && (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                )}
                {savedLayout === group.page && (
                  <span className="text-xs text-green-600 font-medium">Запазено</span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-3">{group.hint}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {group.options.map((option) => {
                  const active = group.current === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => pickLayout(group.page, option.value)}
                      disabled={savingLayout !== null}
                      className={`text-left p-4 rounded-lg border-2 transition-colors disabled:opacity-60 ${
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{option.label}</span>
                        {active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-white">
                            Активно
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{option.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-lg font-semibold mb-1">Секции в началната страница</h2>
        <p className="text-sm text-gray-500 mb-6">
          Плъзнете секциите (или ползвайте стрелките), за да промените реда им в началната страница.
          Изключените секции не се показват на сайта. Hero секцията е винаги първа.
        </p>

        <div className="space-y-3">
          {items.map((item, index) => {
            const def = defMap[item.key];
            if (!def) return null;
            return (
              <div
                key={item.key}
                draggable
                onDragStart={() => setDragged(item.key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(item.key)}
                className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${
                  item.enabled ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 opacity-60'
                }`}
              >
                <GripVertical className="w-5 h-5 text-gray-400 cursor-move flex-shrink-0" />

                <div className="flex flex-col flex-shrink-0">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-primary disabled:opacity-30"
                    title="Нагоре"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                    className="text-gray-400 hover:text-primary disabled:opacity-30"
                    title="Надолу"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex-shrink-0">
                  {index + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{def.label}</div>
                  <div className="text-sm text-gray-500 truncate">{def.description}</div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {def.editorPath ? (
                    <Link
                      to={def.editorPath}
                      className="inline-flex items-center px-3 py-2 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
                      title={def.editorHint}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Редактирай
                    </Link>
                  ) : (
                    <span className="text-xs text-gray-400 px-2">{def.editorHint}</span>
                  )}
                  <button
                    onClick={() => toggle(item.key)}
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                      item.enabled
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                    title={item.enabled ? 'Скрий секцията' : 'Покажи секцията'}
                  >
                    {item.enabled ? (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Видима
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Скрита
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Редът тук определя реда на секциите на началната страница</li>
          <li>• „Скрита" секция не се показва на посетителите</li>
          <li>• „Редактирай" отваря страницата, където се управлява съдържанието на секцията</li>
          <li>• Не забравяйте да натиснете „Запази подредбата"</li>
        </ul>
      </div>
    </div>
  );
};

export default SectionsBuilder;
